
import { supabase } from "@/integrations/supabase/client";
import { weeklyAvailabilityCache } from "@/lib/performance/enhanced-request-cache";
import { createPerformanceLogger } from "@/lib/utils/performance-logger";

const logger = createPerformanceLogger('BatchWeeklyAvailability');

export interface WeeklyAvailabilityWindow {
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

interface WeeklyAvailabilityData {
  [dayOfWeek: number]: WeeklyAvailabilityWindow;
}

/**
 * Batch fetch weekly availability for all 7 days of the week for a user
 */
export async function batchFetchWeeklyAvailability(userId: string): Promise<WeeklyAvailabilityData> {
  // Only log in development to reduce console noise
  if (process.env.NODE_ENV === 'development') {
    console.log(`[batchFetchWeeklyAvailability] Fetching weekly availability for user: ${userId}`);
  }
  
  const cacheKey = `weekly_availability_${userId}`;
  const cached = weeklyAvailabilityCache.get(cacheKey);
  
  if (cached) {
    // Only log in development to reduce console noise
    if (process.env.NODE_ENV === 'development') {
      console.log(`[batchFetchWeeklyAvailability] Cache hit for user ${userId}:`, cached);
    }
    logger.debug(`Weekly availability cache hit for user ${userId}`);
    return cached as WeeklyAvailabilityData;
  }

  logger.debug(`Weekly availability cache miss for user ${userId}, fetching from database`);

  if (userId === 'debug') {
    const debugData: WeeklyAvailabilityData = {};
    for (let day = 0; day < 7; day++) {
      debugData[day] = {
        startTime: "09:00",
        endTime: "17:00",
        isAvailable: day >= 1 && day <= 5 // Monday to Friday
      };
    }
    weeklyAvailabilityCache.set(cacheKey, debugData);
    return debugData;
  }

  try {
    // Fetch all weekly availability data for the user in one query
    const { data, error } = await supabase
      .from('weekly_availability')
      .select('day_of_week, is_available, start_time, end_time')
      .eq('user_id', userId)
      .order('day_of_week')
      .order('slot_sequence');

    if (error) {
      console.log(`[batchFetchWeeklyAvailability] Database error for user ${userId}:`, error);
      logger.error(`Error fetching weekly availability: ${error.message}`);
      // Return default weekday availability
      const defaultData: WeeklyAvailabilityData = {};
      for (let day = 0; day < 7; day++) {
        const isWeekday = day >= 1 && day <= 5;
        defaultData[day] = {
          startTime: "09:00",
          endTime: "17:00",
          isAvailable: isWeekday
        };
      }
      weeklyAvailabilityCache.set(cacheKey, defaultData, 60000); // Short cache for errors
      return defaultData;
    }

  // Only log in development to reduce console noise
  if (process.env.NODE_ENV === 'development') {
    console.log(`[batchFetchWeeklyAvailability] Database response for user ${userId}:`, data);
  }

  const weeklyData: WeeklyAvailabilityData = {};

  // Initialize all days with defaults (weekends unavailable by default)
  for (let day = 0; day < 7; day++) {
    const isWeekday = day >= 1 && day <= 5;
    weeklyData[day] = {
      startTime: "09:00",
      endTime: "17:00",
      isAvailable: isWeekday // Default: weekdays available, weekends not
    };
  }

  // Process the fetched data
  if (data && data.length > 0) {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[batchFetchWeeklyAvailability] Processing ${data.length} weekly availability records for user ${userId}`);
    }
      
      // Group by day of week
      const dayGroups: { [key: number]: typeof data } = {};
      data.forEach(slot => {
        if (!dayGroups[slot.day_of_week]) {
          dayGroups[slot.day_of_week] = [];
        }
        dayGroups[slot.day_of_week].push(slot);
      });

      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[batchFetchWeeklyAvailability] Grouped by day:`, Object.keys(dayGroups).map(day => `${day}: ${dayGroups[parseInt(day)].length} slots`));
      }

      // Process each day
      Object.entries(dayGroups).forEach(([dayStr, slots]) => {
        const dayOfWeek = parseInt(dayStr);
        
        // Check if any slot is available for this day
        const hasAvailableSlot = slots.some(slot => slot.is_available === true);
        
        if (!hasAvailableSlot) {
          weeklyData[dayOfWeek] = {
            startTime: "09:00",
            endTime: "17:00",
            isAvailable: false
          };
          return;
        }

        // Find the earliest start time and latest end time from available slots
        const availableSlots = slots.filter(slot => 
          slot.is_available === true && slot.start_time && slot.end_time
        );
        
        if (availableSlots.length === 0) {
          weeklyData[dayOfWeek] = {
            startTime: "09:00",
            endTime: "17:00",
            isAvailable: true
          };
          return;
        }

        const startTime = availableSlots.reduce((earliest, slot) => 
          slot.start_time < earliest ? slot.start_time : earliest, 
          availableSlots[0].start_time
        );
        
        const endTime = availableSlots.reduce((latest, slot) => 
          slot.end_time > latest ? slot.end_time : latest, 
          availableSlots[0].end_time
        );

        weeklyData[dayOfWeek] = {
          startTime: startTime.substring(0, 5), // Format as HH:MM
          endTime: endTime.substring(0, 5),     // Format as HH:MM
          isAvailable: true
        };
      });
    }

    // Cache the result
    weeklyAvailabilityCache.set(cacheKey, weeklyData);
    
    // Enhanced logging for debugging
    logger.debug(`Cached weekly availability for user ${userId}:`, {
      dataLength: data?.length || 0,
      availableDays: Object.keys(weeklyData).filter(day => weeklyData[parseInt(day)].isAvailable),
      weeklyData: Object.entries(weeklyData).reduce((acc, [day, config]) => {
        acc[day] = {
          isAvailable: config.isAvailable,
          timeRange: config.isAvailable ? `${config.startTime}-${config.endTime}` : 'N/A'
        };
        return acc;
      }, {} as Record<string, any>)
    });
    
    return weeklyData;

  } catch (err) {
    logger.error(`Exception fetching weekly availability: ${err}`);
    // Fallback to reasonable defaults (weekdays only)
    const fallbackData: WeeklyAvailabilityData = {};
    for (let day = 0; day < 7; day++) {
      const isWeekday = day >= 1 && day <= 5;
      fallbackData[day] = {
        startTime: "09:00",
        endTime: "17:00",
        isAvailable: isWeekday
      };
    }
    weeklyAvailabilityCache.set(cacheKey, fallbackData, 60000); // Short cache for errors
    return fallbackData;
  }
}

/**
 * Get availability for a specific day (uses the batched data)
 */
export async function fetchWeeklyAvailabilityWindow(
  userId: string,
  dayOfWeek: number
): Promise<WeeklyAvailabilityWindow> {
  const weeklyData = await batchFetchWeeklyAvailability(userId);
  const dayData = weeklyData[dayOfWeek];
  
  if (!dayData) {
    // Default to weekday availability
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    logger.debug(`No data for day ${dayOfWeek}, defaulting to weekday logic: ${isWeekday}`);
    return {
      startTime: "09:00",
      endTime: "17:00",
      isAvailable: isWeekday
    };
  }
  
  logger.debug(`Weekly availability for user ${userId} day ${dayOfWeek}:`, dayData);
  return dayData;
}

/**
 * Clear weekly availability cache for a specific user
 */
export function clearWeeklyAvailabilityCache(userId: string): void {
  const cacheKey = `weekly_availability_${userId}`;
  weeklyAvailabilityCache.delete(cacheKey);
  logger.debug(`Cleared weekly availability cache for user ${userId}`);
}

/**
 * Get cache statistics
 */
export function getWeeklyAvailabilityCacheStats() {
  return weeklyAvailabilityCache.getStats();
}

/**
 * Force refresh weekly availability for a user (bypass cache)
 */
export async function refreshWeeklyAvailability(userId: string): Promise<WeeklyAvailabilityData> {
  clearWeeklyAvailabilityCache(userId);
  return batchFetchWeeklyAvailability(userId);
}
